import Database from '../types/Database'
import { stripePriceIds } from '../utils/stripe'

/**
 * Subscription Tier Configuration
 *
 * This module defines the configuration for all subscription tiers in the application.
 * It includes capacity limits, pricing, features, and Stripe Price IDs for each tier.
 *
 * Tiers:
 * - Free: 5 users, $0/month (default for all new offices)
 * - Small Office: 20 users, $29/month
 * - Medium Office: 50 users, $59/month
 * - Large Office: 200 users, $199/month
 */

export interface TierConfig {
  id: Database.SubscriptionTier
  name: string
  userCapacity: number
  priceMonthly: number // In cents (e.g., 2900 = $29.00)
  priceAnnual: number // In cents (monthly * 12 * 0.9)
  stripePriceIdMonthly?: string // Stripe Price ID for monthly billing
  stripePriceIdAnnual?: string // Stripe Price ID for annual billing
  features: string[]
}

/**
 * Calculate annual price with 10% discount
 *
 * @param monthlyPrice - Monthly price in cents
 * @returns Annual price in cents (monthly * 12 * 0.9)
 */
export const calculateAnnualPrice = (monthlyPrice: number): number => {
  return Math.round(monthlyPrice * 12 * 0.9)
}

/**
 * Complete configuration for all subscription tiers
 * This is the single source of truth for tier definitions
 */
export const SUBSCRIPTION_TIERS: Record<Database.SubscriptionTier, TierConfig> =
  {
    free: {
      id: 'free',
      name: 'Free',
      userCapacity: 5,
      priceMonthly: 0,
      priceAnnual: 0,
      features: [
        'Up to 5 users',
        'Unlimited desks',
        'Basic booking features',
        'Office notifications'
      ]
    },
    small: {
      id: 'small',
      name: 'Small Office',
      userCapacity: 20,
      priceMonthly: 2900, // $29/month
      priceAnnual: calculateAnnualPrice(2900), // $313.20/year ($26.10/month)
      stripePriceIdMonthly: stripePriceIds.small.monthly,
      stripePriceIdAnnual: stripePriceIds.small.annual,
      features: [
        'Up to 20 users',
        'Unlimited desks',
        'All booking features',
        'Office notifications',
        'Priority support'
      ]
    },
    medium: {
      id: 'medium',
      name: 'Medium Office',
      userCapacity: 50,
      priceMonthly: 5900, // $59/month
      priceAnnual: calculateAnnualPrice(5900), // $636.60/year ($53.05/month)
      stripePriceIdMonthly: stripePriceIds.medium.monthly,
      stripePriceIdAnnual: stripePriceIds.medium.annual,
      features: [
        'Up to 50 users',
        'Unlimited desks',
        'All booking features',
        'Office notifications',
        'Priority support',
        'Advanced analytics'
      ]
    },
    large: {
      id: 'large',
      name: 'Large Office',
      userCapacity: 200,
      priceMonthly: 19900, // $199/month
      priceAnnual: calculateAnnualPrice(19900), // $2,149.20/year ($179.10/month)
      stripePriceIdMonthly: stripePriceIds.large.monthly,
      stripePriceIdAnnual: stripePriceIds.large.annual,
      features: [
        'Up to 200 users',
        'Unlimited desks',
        'All booking features',
        'Office notifications',
        'Priority support',
        'Advanced analytics',
        'Dedicated account manager'
      ]
    }
  }

/**
 * Get Stripe Price ID for tier and billing interval
 *
 * @param tier - The subscription tier
 * @param billingInterval - The billing interval ('monthly' or 'annual')
 * @returns The Stripe Price ID or undefined if not configured
 */
export const getStripePriceId = (
  tier: Database.SubscriptionTier,
  billingInterval: 'monthly' | 'annual'
): string | undefined => {
  const tierConfig = SUBSCRIPTION_TIERS[tier]
  if (!tierConfig) {
    return undefined
  }
  return billingInterval === 'monthly'
    ? tierConfig.stripePriceIdMonthly
    : tierConfig.stripePriceIdAnnual
}

/**
 * Format annual price to monthly equivalent
 *
 * @param annualPrice - Annual price in cents
 * @returns Formatted string showing monthly equivalent (e.g., "26.10/mo")
 */
export const formatAnnualAsMonthly = (annualPrice: number): string => {
  const monthlyEquivalent = annualPrice / 12
  return `${(monthlyEquivalent / 100).toFixed(2)}/mo`
}

/**
 * Get tier configuration by tier ID
 *
 * @param tierId - The tier identifier ('free', 'small', 'medium', 'large')
 * @returns The tier configuration object
 * @throws Error if tier ID is invalid
 */
export const getTierById = (tierId: Database.SubscriptionTier): TierConfig => {
  const tier = SUBSCRIPTION_TIERS[tierId]
  if (!tier) {
    throw new Error(`Invalid tier ID: ${tierId}`)
  }
  return tier
}

/**
 * Get tier configuration by user capacity
 * Returns the tier that matches the exact capacity, or null if no match
 *
 * @param capacity - The user capacity to look up
 * @returns The tier configuration object or null if no exact match
 */
export const getTierByCapacity = (capacity: number): TierConfig | null => {
  const tiers = Object.values(SUBSCRIPTION_TIERS)
  return tiers.find(tier => tier.userCapacity === capacity) || null
}

/**
 * Get all tier configurations as an array
 * Useful for displaying all tiers in the UI (e.g., pricing page)
 *
 * @returns Array of all tier configurations in order (free, small, medium, large)
 */
export const getAllTiers = (): TierConfig[] => {
  return [
    SUBSCRIPTION_TIERS.free,
    SUBSCRIPTION_TIERS.small,
    SUBSCRIPTION_TIERS.medium,
    SUBSCRIPTION_TIERS.large
  ]
}

/**
 * Get only paid tiers (excludes free tier)
 * Useful for subscription purchase flows
 *
 * @returns Array of paid tier configurations
 */
export const getPaidTiers = (): TierConfig[] => {
  return [
    SUBSCRIPTION_TIERS.small,
    SUBSCRIPTION_TIERS.medium,
    SUBSCRIPTION_TIERS.large
  ]
}

/**
 * Format price in cents to display string
 *
 * @param priceInCents - Price in cents (e.g., 2900)
 * @returns Formatted price string (e.g., "$29")
 */
export const formatPrice = (priceInCents: number): string => {
  if (priceInCents === 0) {
    return 'Free'
  }
  const dollars = priceInCents / 100
  return `$${dollars.toFixed(0)}`
}

/**
 * Check if a tier is a paid tier
 *
 * @param tierId - The tier identifier to check
 * @returns true if the tier requires payment, false for free tier
 */
export const isPaidTier = (tierId: Database.SubscriptionTier): boolean => {
  return tierId !== 'free'
}

/**
 * Get the next higher tier for upgrade suggestions
 *
 * @param currentTier - The current tier identifier
 * @returns The next tier configuration or null if already at highest tier
 */
export const getNextTier = (
  currentTier: Database.SubscriptionTier
): TierConfig | null => {
  const tierOrder: Database.SubscriptionTier[] = [
    'free',
    'small',
    'medium',
    'large'
  ]
  const currentIndex = tierOrder.indexOf(currentTier)

  if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
    return null
  }

  return SUBSCRIPTION_TIERS[tierOrder[currentIndex + 1]]
}

/**
 * Check if a tier upgrade is valid
 *
 * @param fromTier - Current tier
 * @param toTier - Target tier
 * @returns true if the upgrade is valid (moving to a higher tier)
 */
export const isValidUpgrade = (
  fromTier: Database.SubscriptionTier,
  toTier: Database.SubscriptionTier
): boolean => {
  const tierOrder: Database.SubscriptionTier[] = [
    'free',
    'small',
    'medium',
    'large'
  ]
  const fromIndex = tierOrder.indexOf(fromTier)
  const toIndex = tierOrder.indexOf(toTier)

  return fromIndex !== -1 && toIndex !== -1 && toIndex > fromIndex
}

/**
 * Check if a tier change is a downgrade
 *
 * @param fromTier - Current tier
 * @param toTier - Target tier
 * @returns true if the change is a downgrade (moving to a lower tier)
 */
export const isDowngrade = (
  fromTier: Database.SubscriptionTier,
  toTier: Database.SubscriptionTier
): boolean => {
  const tierOrder: Database.SubscriptionTier[] = [
    'free',
    'small',
    'medium',
    'large'
  ]
  const fromIndex = tierOrder.indexOf(fromTier)
  const toIndex = tierOrder.indexOf(toTier)

  return fromIndex !== -1 && toIndex !== -1 && toIndex < fromIndex
}
